import { createSelector } from "reselect";
import { DictionaryParametersTypes } from "./global.actions";
import { getNutrientsProcessVariables, getProcessVariablesOptions } from "./global.utils";
export const getGlobalState = (state) => state.global;
export const isInitedSelector = createSelector(getGlobalState, (state) => state.isInited || false);
export const isOfflineSelector = createSelector(getGlobalState, (state) => state.isOffline);
export const lastRequestedRouteSelector = createSelector(getGlobalState, (state) => state.lastRequestedRoute);
export const isNoConnectionDialogOpenSelector = createSelector(getGlobalState, (state) => state.noConnectionDialogOpen);
export const decisionTreeSelector = createSelector(getGlobalState, (state) => state.decisionTree || {});
export const decisionTreeCurrentStepSelector = createSelector(getGlobalState, (state) => state.decisionTreeCurrentStep);
export const decisionTreePreviousStepSelector = createSelector(getGlobalState, (state) => state.decisionTreePreviousStep);
export const decisionTreeSelectedStepsSelector = createSelector(getGlobalState, (state) => state.decisionTreeSelectedSteps || []);
export const decisionTreeLoadingStatusSelector = createSelector(getGlobalState, (state) => state.decisionTreeLoading || false);
export const decisionTreeModalOpenSelector = createSelector(getGlobalState, (state) => state.decisionTreeModalOpened || false);
export const rsEntityCreatingModalOpenedSelector = createSelector(getGlobalState, (state) => state.rsEntityCreatingModalOpened || false);
export const detailsKnowledgeItemToViewSelector = createSelector(getGlobalState, (state) => state.detailsKnowledgeItemToView);
export const parametersDictionarySelector = (type) => createSelector(getGlobalState, (state) => {
    switch (type) {
        case DictionaryParametersTypes.AbsoluteTemplate:
            return state.AbsoluteTemplateParameters || {};
        case DictionaryParametersTypes.RelativeTemplate:
            return state.RelativeTemplateParameters || {};
        case DictionaryParametersTypes.Optimisation:
            return state.OptimisationParameters || {};
        case DictionaryParametersTypes.KnownValue:
            return state.KnownValueParameters || {};
        case DictionaryParametersTypes.Result0D:
            return state.Result0DParameters || {};
        case DictionaryParametersTypes.ResultDependent:
            return state.ResultDependentParameters || {};
        case DictionaryParametersTypes.ResultIndependent:
            return state.ResultIndependentParameters || {};
        default:
            return {};
    }
});
export const parameterUnitsOptionsSelector = (paramId, type) => createSelector(parametersDictionarySelector(type), (knownValuesDictionary) => {
    if (!paramId) {
        return [];
    }
    const targetDictionary = knownValuesDictionary?.[paramId] || {};
    return ((targetDictionary?.units || []).reduce((acc, { name, id }) => {
        if (name) {
            acc.push({ label: name, value: id });
        }
        return acc;
    }, []) || []);
});
export const optimisationParametersOptionsSelector = (knownValues) => createSelector(parametersDictionarySelector(DictionaryParametersTypes.Optimisation), (optimisationParametersDictionary) => Object.values(optimisationParametersDictionary).reduce((list, { name, id }) => {
    if (!knownValues.includes(id)) {
        list.push({
            label: name,
            value: id,
        });
    }
    return list;
}, []));
export const knownValueValuesOptionsSelector = (valuesForOptimisations) => createSelector(parametersDictionarySelector(DictionaryParametersTypes.KnownValue), (parametersDictionary) => Object.values(parametersDictionary).reduce((list, { name, id }) => {
    if (!valuesForOptimisations.includes(id)) {
        list.push({
            label: name,
            value: id,
        });
    }
    return list;
}, []));
export const metricTemplateParameterOptionsSelector = (type, withUnknown) => createSelector(parametersDictionarySelector(DictionaryParametersTypes.AbsoluteTemplate), parametersDictionarySelector(DictionaryParametersTypes.RelativeTemplate), (absoluteTemplateParameters, relativeTemplateParameters) => {
    const parameters = type === "simple" ? absoluteTemplateParameters : relativeTemplateParameters;
    const options = Object.keys(parameters).map((key) => ({
        label: parameters[key].name,
        value: parameters[key].id,
        defaultUnits: parameters[key].defaultUnits,
    }));
    const unknownOption = { label: "Unknown", value: "" };
    return withUnknown ? [unknownOption, ...options] : options;
});
export const simpleMetricParameterUnitsOptionsSelector = (paramId) => createSelector(getGlobalState, (state) => {
    if (!paramId) {
        return [];
    }
    const rawUnits = state.AbsoluteTemplateParameters[paramId];
    return rawUnits?.units.map(({ name, id }) => ({ label: name, value: id })) || [];
});
export const relativeMetricParameterUnitsOptionsSelector = (paramId) => createSelector(getGlobalState, (state) => {
    if (!paramId) {
        return [];
    }
    const rawUnits = state.RelativeTemplateParameters[paramId];
    return rawUnits?.units.map(({ name, id }) => ({ label: name, value: id })) || [];
});
export const bioreactorByIdSelector = (bioreactorId) => createSelector(getGlobalState, (state) => state.Bioreactors.find(({ id }) => bioreactorId === id) || {});
export const bioreactorDetailsSelector = (bioreactorId, spargerId, impellerId) => createSelector(getGlobalState, (state) => {
    const { name, permittedImpellers = [], permittedSpargers = [], } = (state.Bioreactors || []).find(({ id }) => id === bioreactorId) ||
        {};
    const impeller = permittedImpellers.find(({ id }) => id === impellerId) || {};
    const sparger = permittedSpargers.find(({ id }) => id === spargerId) || {};
    return { bioreactor: name, impeller: impeller.name, sparger: sparger.name };
});
export const bioreactorsOptionsSelector = createSelector(getGlobalState, (state) => state.Bioreactors.map(({ id, name }) => ({
    label: name,
    value: id,
})));
export const bioreactorParametersOptionsSelector = createSelector(getGlobalState, (state) => state.bioreactorParameters);
export const metricTemplatesSelector = createSelector(getGlobalState, (state) => state.MetricTemplates || {});
export const metricTemplatesByIdSelector = (paramId, type) => createSelector(getGlobalState, (state) => Object.values(state.MetricTemplates).filter((template) => template.type === type && template.parameter.id === paramId) || []);
export const metricTemplatesOptionsSelector = createSelector(getGlobalState, (state) => Object.values(state.MetricTemplates).map(({ id, name, isRatiometric }) => ({
    label: name,
    value: id,
    isRatiometric,
})));
export const editedMetricTemplatesOptionsSelector = (paramId, type) => createSelector(metricTemplatesByIdSelector(paramId, type), (templates) => Object.values(templates).map(({ id, name, isRatiometric, range }) => ({
    label: name,
    value: id,
    isRatiometric,
    range,
})));
export const spargerOptionsSelector = (bioreactorId) => createSelector(getGlobalState, (state) => {
    const bioreactor = state.Bioreactors.find(({ id }) => id === bioreactorId);
    return bioreactor?.permittedSpargers.map(({ id, name }) => ({ label: name, value: id })) || [];
});
export const impellerOptionsSelector = (bioreactorId) => createSelector(getGlobalState, (state) => {
    const bioreactor = state.Bioreactors.find(({ id }) => id === bioreactorId);
    return bioreactor?.permittedImpellers.map(({ id, name }) => ({ label: name, value: id })) || [];
});
export const bioreactorsListSelector = createSelector(getGlobalState, (state) => state.Bioreactors || []);
export const renamingProgressSelector = createSelector(getGlobalState, (state) => state.renamingInProgress || false);
export const scrollElementSelector = (element) => createSelector(getGlobalState, (state) => element && state.scrollElements[element]);
export const appVersionsSelector = createSelector(getGlobalState, (state) => state.appVersions);
export const psTagsListSelector = createSelector(getGlobalState, (state) => state.psTags.map((tag) => ({ value: tag.id, label: tag.name, removable: true })));
export const rsTagsListSelector = createSelector(getGlobalState, (state) => state.rsTags.map((tag) => ({ value: tag.id, label: tag.name, removable: !tag.persistent })));
export const rsSystemTagsNamesSelector = createSelector(rsTagsListSelector, (tags) => tags.filter(({ removable }) => !removable).map(({ label }) => label));
export const processVariablesSelector = createSelector(getGlobalState, (state) => state.processVariables);
export const nutrientsProcessVariablesSelector = createSelector(processVariablesSelector, (processVariables) => getNutrientsProcessVariables(processVariables));
export const processVariablesOptionsSelector = createSelector(processVariablesSelector, (processVariables) => getProcessVariablesOptions(processVariables));
export const parameterTypesSelector = createSelector(getGlobalState, (state) => state.parameterTypes);
